import React, { useState, useEffect, useContext } from "react";
import { Table, Button, Modal } from "react-bootstrap";
import NotyfContext from "../contexts/NotyfContext";
import authenticatedFetch from "./fetch"

import { UserSettingsContext } from "../contexts/UserSettingsContext";

import LinkedDocumentsDisplay from "../utils/LinkedDocumentsDisplay";
import LinkedDocumentSelect from "../utils/LinkedDocumentSelect";
import { getDocumentDisplayLabel } from "../utils/linkedDocumentUtils";

import {  Edit2, X, Check} from "react-feather";


const ParticipationDocumentsModal = ({ show, onClose, entityType, entityId, entityName, providerId, providerName, onDocumentCountLoaded, practiceId=0, practiceName=''}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState({});
  const [editingDocuments, setEditingDocuments] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [documentFilenames, setDocumentFilenames] = useState([]);

  const notyf = useContext(NotyfContext);
  const { isH3Admin, authorities, documentsRefresh, setDocumentsRefresh } = useContext(UserSettingsContext);

  const canEdit= isH3Admin || authorities.includes("PROVIDER_DOCUMENT_EDIT"); 

  const handleEditDocumentsClicked = () => {
    setEditingDocuments(true);
  }
  
  const handleSaveDocumentsClicked = () => {
    setEditingDocuments(false);
    doSave();
  }

  const handleCancelEditDocumentsClicked = () => {
    setEditingDocuments(false);
    setInitialDocumentFilenames();
  }
  
  const setInitialDocumentFilenames = () => {
    let initialDocumentFilenames = []
    let initialSelectedDocuments = []
    if (isLoaded && !error && data && data.documents)
    {
      for (var i=0; i<data.documents.length; i++)
      {
        initialDocumentFilenames.push(data.documents[i].filename);
        initialSelectedDocuments.push({value: data.documents[i].filename, label: getDocumentDisplayLabel(data.documents[i])});
      }
    }

    setDocumentFilenames(initialDocumentFilenames);
    setSelectedDocuments(initialSelectedDocuments);
  }

  useEffect(() => {
      setInitialDocumentFilenames();
  }, [data, isLoaded, error]);

  useEffect(() => {
    // Load every time, so we get the document count; don't gate on show
    authenticatedFetch({
      path: entityType === "facility" ? "provider/" + providerId + "/facility/" + entityId + "/facilitydocuments" :  "provider/" + providerId + "/practice/" + practiceId + "/carrier/" + entityId + "/carrierdocuments",
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);

        if (onDocumentCountLoaded) {
          onDocumentCountLoaded(result.documents?.length ?? 0);
        }
      },
      errorHandler: function() {
        setIsLoaded(true);
        setError(true);
      }
    });
  }, [practiceId, providerId, entityId, documentsRefresh])
  
  const handleClose = () => {
    setEditingDocuments(false);
    onClose();
  }

  const doSave = () => {
    authenticatedFetch({
      path: entityType === "facility" ? "provider/" + providerId + "/facility/" + entityId + "/facilitydocuments" : "provider/" + providerId + "/practice/" + practiceId + "/carrier/" + entityId + "/carrierdocuments",
      method: "PUT",
      requestData: {documentFilenames: documentFilenames},
      successHandler: function(result) {
        setIsLoaded(true);
        setDocumentsRefresh(documentsRefresh + 1);
        notyf.open({type: "success", message: "Saved!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
        setData(result);
      },
      errorHandler: function() {
        setIsLoaded(true);
        setError(true);
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  const handleDocumentChanged = (documents, e) => {
    setSelectedDocuments(documents);

    let newDocumentFilenames = []
    for (var i=0; i<documents.length; i++)
    {
      newDocumentFilenames.push(documents[i].value);
    }

    setDocumentFilenames(newDocumentFilenames);
  }

  if (error) {
    return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton style={{backgroundColor: '#DEF2F1'}}>
        <Modal.Title>
          <span style={{fontSize: "0.9em", fontWeight: "normal"}}>Documents for {providerName}</span><br />{practiceName === '' ? '' : practiceName + ' | '} {entityName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        Something went wrong; please reload the page...
      </Modal.Body>
      <Modal.Footer style={{backgroundColor: '#DEF2F1'}}>
        <Button onClick={handleClose}>Close</Button>
      </Modal.Footer>
    </Modal>
    );
  }
  if (!isLoaded) {
    return (
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton style={{backgroundColor: '#DEF2F1'}}>
          <Modal.Title>
            <span style={{fontSize: "0.9em", fontWeight: "normal"}}>Documents for {providerName}</span><br />{practiceName === '' ? '' : practiceName + ' | '} {entityName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Loading...
        </Modal.Body>
        <Modal.Footer style={{backgroundColor: '#DEF2F1'}}>
          <Button onClick={handleClose}>Close</Button>
        </Modal.Footer>
      </Modal>
      );
  }

  return (
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton style={{backgroundColor: '#DEF2F1'}}>
          <Modal.Title>
            <span style={{fontSize: "0.9em", fontWeight: "normal"}}>Documents for {providerName}</span><br />{practiceName === '' ? '' : practiceName + ' | '} {entityName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table> 
            <tbody>
              <tr>
                <td>
                  Documents
                </td>
                <td>
                  {editingDocuments ?
                  <LinkedDocumentSelect entityType={entityType} providerId={providerId} handleDocumentChanged={handleDocumentChanged} selectedDocuments={selectedDocuments}/>
                  :
                  (entityType === "facility" ?
                    <LinkedDocumentsDisplay entityType={"provider/" + providerId + "/facility"} documents={data.documents} entityId={entityId}/>
                  : <LinkedDocumentsDisplay entityType={"provider/" + providerId + "/practice/" + practiceId + "/carrier"} documents={data.documents} entityId={entityId}/>
                  )
                  } 
                </td>
                <td>
                  {editingDocuments ?
                   <>
                    <X cursor="pointer" style={{'height': '16px', 'width': '16px'}} onClick={() => {handleCancelEditDocumentsClicked()}}/>&nbsp;
                    <Check cursor="pointer" style={{'height': '16px', 'width': '16px'}} onClick={() => {handleSaveDocumentsClicked()}} className="text-success"/>
                  </>
                  : (canEdit ? <Edit2 style={{'height': '12px', 'width': '12px'}} cursor="pointer" onClick={() => {handleEditDocumentsClicked()}}/>: <></>)
                  }
                </td>
              </tr>
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer style={{backgroundColor: '#DEF2F1'}}>
          <Button onClick={handleClose}>Close</Button>
        </Modal.Footer>
      </Modal>
  );
}


export default ParticipationDocumentsModal;
